import { useLoadingStore } from '@/modules/shared/stores/loadingStore'
import moment from 'moment'
import type { Moment } from 'moment'
import { axiosInstance } from '@/modules/shared/lib/axios'
import { SERVICE_DATE_FORMAT, SERVICE_TIME_FORMAT } from '@/app/constants/date'
import { AlertModel } from '@/app/models/alert/alertModel'
import { SingleAlertType } from '@/app/constants/singleAlertType'

const useAlertService = () => {
  const startLoading = useLoadingStore((s) => s.startLoading)
  const getAlerts = async ({
    companyId,
    drivers,
    dateFrom,
    dateTo,
    isDriver,
    upcoming,
    onlyVisible,
    showLoading = true,
  }: {
    companyId: string
    drivers: Array<string> | null
    dateFrom: Moment | string
    dateTo: Moment | string
    isDriver: boolean
    upcoming: boolean
    onlyVisible: boolean
    showLoading?: boolean
  }): Promise<Array<AlertModel>> => {
    const stopLoading = showLoading ? startLoading() : () => {}
    try {
      const dateFromStr =
        typeof dateFrom === 'string'
          ? dateFrom
          : moment(dateFrom).format(SERVICE_DATE_FORMAT)
      const dateToStr =
        typeof dateTo === 'string'
          ? dateTo
          : moment(dateTo).format(SERVICE_DATE_FORMAT)

      const res = await axiosInstance.post(
        `/api/${companyId}/alert/alerts`,
        {
          drivers: drivers && drivers.length > 0 ? drivers : null,
          dateFrom: dateFromStr,
          dateTo: dateToStr,
          isDriver,
          upcoming,
          onlyVisible,
        },
      )

      const data = res.data?.data || res.data || []
      
      // Map the response to AlertModel format
      const alerts: Array<AlertModel> = data.map((alert: any, index: number) => ({
        type: alert.type,
        eventTime: moment(alert.eventTime),
        remindTime: alert.remindTime ? moment(alert.remindTime) : null,
        object: alert.object || '-',
        text: alert.text || alert.description || '',
        driverId: alert.driverId ? String(alert.driverId) : '',
        vehicleId: alert.vehicleId ? String(alert.vehicleId) : '',
        alertId: alert.id ? String(alert.id) : alert.alertId || '0',
        notRemindAnymore: alert.notRemindAnymore || false,
        index,
      }))

      return alerts
    } catch (err) {
      console.error('Error fetching alerts:', err)
      return []
    } finally {
      stopLoading()
    }
  }

  const addEditAlert = async ({
    companyId,
    alertId,
    driverId,
    vehicleId,
    eventTime,
    remindTime,
    type,
    text,
    notRemindAnymore,
    showLoading = true,
  }: {
    companyId: string
    alertId: string
    driverId: string
    vehicleId: string
    eventTime: Moment | string
    remindTime: Moment | string | null
    type: SingleAlertType
    text: string
    notRemindAnymore: boolean
    showLoading?: boolean
  }): Promise<boolean> => {
    const stopLoading = showLoading ? startLoading() : () => {}
    try {
      const eventTimeStr =
        typeof eventTime === 'string'
          ? eventTime
          : moment(eventTime).format(SERVICE_TIME_FORMAT)
      const remindTimeStr = remindTime
        ? typeof remindTime === 'string'
          ? remindTime
          : moment(remindTime).format(SERVICE_TIME_FORMAT)
        : null

      await axiosInstance.post(`/api/${companyId}/alert/addEditAlert`, {
        alertId,
        driverId,
        vehicleId,
        eventTime: eventTimeStr,
        remindTime: remindTimeStr,
        type: SingleAlertType[type],
        text,
        notRemindAnymore,
      })

      return true
    } catch (err) {
      console.error('Error adding/editing alert:', err)
      return false
    } finally {
      stopLoading()
    }
  }

  return {
    getAlerts,
    addEditAlert,
  }
}

export default useAlertService

